import { Fragment, useState } from 'react';
import { generalStyle } from 'helper/Style';
import { ObjectType, OperationType, ModalTextSet, getModalText } from 'helper/ModalText';
import Modal, { modalStyle } from 'component/part/Modal';

export type ConfirmResultModalProps = {
  /** 確認モーダルの表示状態（外部から制御） */
  display: boolean;
  /** 確認モーダルを閉じる（キャンセル時も含む） */
  onHide: () => void;
  /**
   * 確認後に実行する非同期処理。
   * true を返すと完了モーダル、false を返すとエラーモーダルを表示する。
   * throw した場合もエラーモーダルを表示する（想定外エラー対応）。
   */
  onSubmit: () => Promise<boolean>;
  /** 完了モーダルで OK を押した後に呼ばれるコールバック（画面遷移など） */
  onOk?: () => void;
  objectType: ObjectType;
  operationType: OperationType;
  /** テキストの個別上書き。objectType/operationType のデフォルト文言を部分的に差し替えたい場合に使用 */
  textOverride?: Partial<ModalTextSet>;
};

const ConfirmResultModal = ({
  display,
  onHide,
  onSubmit,
  onOk,
  objectType,
  operationType,
  textOverride,
}: ConfirmResultModalProps) => {
  const [resultStatus, setResultStatus] = useState<'done' | 'error' | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const texts = { ...getModalText(objectType, operationType), ...textOverride };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    onHide();
    try {
      const success = await onSubmit();
      setResultStatus(success ? 'done' : 'error');
    } catch {
      setResultStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOk = () => {
    const wasDone = resultStatus === 'done';
    setResultStatus(null);
    if (wasDone) {
      onOk?.();
    }
  };

  return <Fragment>
    <Modal display={display} onHide={onHide}>
      <div>{texts.confirm}</div>
      <div css={modalStyle.buttonArea}>
        <button onClick={handleSubmit} disabled={submitting} css={generalStyle.button('normal', submitting)}>{texts.submit}</button>
        <button onClick={onHide} css={generalStyle.button('warning')}>キャンセル</button>
      </div>
    </Modal>
    <Modal display={!!resultStatus} onHide={handleOk}>
      <div>{resultStatus === 'done' ? texts.done : texts.error}</div>
      <div css={modalStyle.buttonArea}>
        <button onClick={handleOk} css={generalStyle.button()}>OK</button>
      </div>
    </Modal>
  </Fragment>;
};

export default ConfirmResultModal;
